import { FilterColumnDef, FilterRow, FilterValue, OPERATORS_BY_TYPE } from './types';

/** 行と行のあいだに挟む語。旧画面の条件表示（logicalOperator をそのまま出す）に合わせる。 */
const COMBINATOR_TEXTS: Record<FilterRow['combinator'], string> = { where: '', and: 'and', or: 'or' };

/**
 * 候補の value → label。idSelect / enumSelect 以外は空の Map を返す。
 * 2 つの型を 1 つの条件でまとめると options が union 配列になり map の型が付かないため分けている。
 */
function optionLabels(column: FilterColumnDef): Map<string | number, string> {
  if (column.type === 'enumSelect') {
    return new Map<string | number, string>(column.options.map((o) => [o.value, o.label]));
  }
  if (column.type === 'idSelect') {
    return new Map<string | number, string>(column.options.map((o) => [o.value, o.label]));
  }
  return new Map<string | number, string>();
}

/** 条件の値を人が読める文字列にする。値入力を持たない演算子（空である等）は空文字。 */
export function formatFilterValue(column: FilterColumnDef, value: FilterValue): string {
  switch (value.kind) {
    case 'text':
      return `"${value.value}"`;
    case 'number':
      return value.value === null ? '' : `${value.value}`;
    case 'numberRange':
      return `${value.min ?? ''}〜${value.max ?? ''}`;
    case 'time':
      return value.value;
    case 'enum': {
      const labels = optionLabels(column);
      return value.values.map((v) => labels.get(v) ?? v).join(', ');
    }
    case 'id': {
      // トラック・ドライバーの候補を渡せない画面（シナリオ履歴など）では名前が引けない。
      // ID をそのまま並べても読めないので、1 件も引けないときだけ件数で出す
      const labels = optionLabels(column);
      if (value.values.length > 0 && value.values.every((v) => !labels.has(v))) return `${value.values.length} 件`;
      return value.values.map((v) => labels.get(v) ?? `${v}`).join(', ');
    }
    case 'boolean':
      if (value.value === null) return '';
      return value.value ? '可' : '不可';
    default:
      return '';
  }
}

/**
 * 条件 1 行を「列ラベル 演算子ラベル 値」の 1 文にする。
 * 列を引けないときはキーをそのまま返し、消えた項目に気づけるようにする。
 */
export function filterRowText(row: FilterRow, columnsByKey: Map<string, FilterColumnDef>): string {
  const column = columnsByKey.get(row.column);
  if (!column) return row.column;
  const operatorLabel = OPERATORS_BY_TYPE[column.type].find((o) => o.value === row.operator)?.label ?? row.operator;
  return `${column.label} ${operatorLabel} ${formatFilterValue(column, row.value)}`.trimEnd();
}

/** 条件行をまとめて 1 文にする。先頭行の Where は出さず、2 行目以降に and / or を挟む。 */
export function filterRowsText(rows: FilterRow[], columnsByKey: Map<string, FilterColumnDef>): string {
  return rows
    .map((row, idx) => [idx === 0 ? '' : COMBINATOR_TEXTS[row.combinator], filterRowText(row, columnsByKey)].filter((it) => it).join(' '))
    .join(' ');
}
